import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BadgeIcon from '@mui/icons-material/Badge';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import type { SvgIconComponent } from '@mui/icons-material';

import { ManagedLeadProductPath } from '../services/api/managed-leads/managedLeads';

export interface ManagedLeadCategory {
  id: 'in-progress' | 'won' | 'dropped';
  name: 'In gestione' | 'Vinte' | 'Drop';
  path: string;
  icon: SvgIconComponent;
}

export interface ManagedLeadProduct {
  id: 'mortgages' | 'loans' | 'cdq';
  name: 'Mutui' | 'Prestiti' | 'Cessioni del quinto';
  icon: SvgIconComponent;
  activePaths: string[];
  categories: ManagedLeadCategory[];
  detailPath: string;
  /**
   * Product segment of the Tasso Quinto API. Mutui has none: it is served by
   * another backend, and nothing addresses it by an API product path.
   */
  apiPath?: ManagedLeadProductPath;
}

const createCategories = (
  paths: Record<ManagedLeadCategory['id'], string>
): ManagedLeadCategory[] => [
  {
    id: 'in-progress',
    name: 'In gestione',
    path: paths['in-progress'],
    icon: HourglassTopIcon,
  },
  {
    id: 'won',
    name: 'Vinte',
    path: paths.won,
    icon: EmojiEventsIcon,
  },
  {
    id: 'dropped',
    name: 'Drop',
    path: paths.dropped,
    icon: CloseIcon,
  },
];

export const MANAGED_LEAD_PRODUCTS: ManagedLeadProduct[] = [
  {
    id: 'mortgages',
    name: 'Mutui',
    icon: HomeWorkIcon,
    activePaths: ['/lead', '/lead-closed', '/lead-dropped'],
    categories: createCategories({
      'in-progress': '/lead',
      won: '/lead-closed',
      dropped: '/lead-dropped',
    }),
    detailPath: '/lead/:id',
  },
  {
    id: 'loans',
    name: 'Prestiti',
    icon: AccountBalanceIcon,
    activePaths: ['/prestiti'],
    categories: createCategories({
      'in-progress': '/prestiti/in-progress',
      won: '/prestiti/won',
      dropped: '/prestiti/dropped',
    }),
    detailPath: '/prestiti/deals/:dealId',
    apiPath: 'prestiti',
  },
  {
    id: 'cdq',
    name: 'Cessioni del quinto',
    icon: BadgeIcon,
    activePaths: ['/cessioni-del-quinto'],
    categories: createCategories({
      'in-progress': '/cessioni-del-quinto/in-progress',
      won: '/cessioni-del-quinto/won',
      dropped: '/cessioni-del-quinto/dropped',
    }),
    detailPath: '/cessioni-del-quinto/deals/:dealId',
    apiPath: 'cessioni-del-quinto',
  },
];

export const NON_MORTGAGE_MANAGED_LEAD_PRODUCTS = MANAGED_LEAD_PRODUCTS.filter(
  (product) => product.id !== 'mortgages'
);

/**
 * Where each Tasso Quinto product's detail lives, derived from the route the
 * router registers. Cards and queues link to a lead through this, so renaming
 * a route stays one edit.
 */
export const MANAGED_LEAD_DETAIL_BASE_PATHS = Object.fromEntries(
  MANAGED_LEAD_PRODUCTS.filter((product) => product.apiPath).map((product) => [
    product.apiPath,
    product.detailPath.replace('/:dealId', ''),
  ])
) as Record<ManagedLeadProductPath, string>;
